// スマートガイド（整列補助線）を描画するオーバーレイ。
//
// ドラッグ移動・複製中に useAlignmentGuideStore へ積まれたガイドを、ステージ座標のまま
// 細い線として描画する。線の太さはズーム非依存（画面上で常に約 1px）に見えるよう 1/zoom で
// 補正する。ヒットテストには関与しないため pointerEvents: none。

import { useCanvasStore } from "./store/canvas-store";
import { useAlignmentGuideStore } from "./store/canvas-alignment-store";

// PowerPoint / FigJam に倣い、視認性の高いマゼンタ系の線色にする。
const GUIDE_COLOR = "#f2147a";

export function CanvasAlignmentGuidesLayer(): JSX.Element | null {
  const guides = useAlignmentGuideStore((s) => s.guides);
  const zoom = useCanvasStore((s) => s.view.zoom);

  if (guides.length === 0) return null;

  // 画面上で常に約 1px に見える線幅（ステージ座標へ換算）。最低 0.5px は確保する。
  const lineThickness = Math.max(0.5, 1 / zoom);

  return (
    <div
      data-testid="canvas-alignment-guides"
      style={{
        position: "absolute",
        inset: 0,
        pointerEvents: "none",
        // 選択オーバーレイ（MAX_SAFE_INTEGER）より僅かに下、各オブジェクトより上に出す。
        zIndex: Number.MAX_SAFE_INTEGER - 2,
      }}
    >
      {guides.map((guide, index) => {
        const length = Math.max(0, guide.end - guide.start);
        const style =
          guide.axis === "x"
            ? {
                // 縦線: x 固定、start..end の縦方向へ伸ばす。
                left: `${guide.position - lineThickness / 2}px`,
                top: `${guide.start}px`,
                width: `${lineThickness}px`,
                height: `${length}px`,
              }
            : {
                // 横線: y 固定、start..end の横方向へ伸ばす。
                left: `${guide.start}px`,
                top: `${guide.position - lineThickness / 2}px`,
                width: `${length}px`,
                height: `${lineThickness}px`,
              };
        // サイズ一致（寸法一致）ガイドは、端・中心の整列（実線）と区別するため破線で描く。
        // background では破線にできないため、size は該当辺方向へ dashed の border を引く。
        const isSize = guide.variant === "size";
        const dash =
          isSize
            ? guide.axis === "x"
              ? { borderLeft: `${lineThickness}px dashed ${GUIDE_COLOR}`, width: 0 }
              : { borderTop: `${lineThickness}px dashed ${GUIDE_COLOR}`, height: 0 }
            : { background: GUIDE_COLOR };
        return (
          <div
            key={`${guide.variant ?? "align"}-${guide.axis}-${guide.position}-${index}`}
            style={{
              position: "absolute",
              ...style,
              ...dash,
            }}
          />
        );
      })}
    </div>
  );
}
